import { supabase } from '../supabaseClient';
import { connectionService } from './connectionService';

interface SharePointConfig {
  siteId: string;
  driveId: string;
  templatesFolder: string;
  generatedContractsFolder: string;
  signedContractsFolder: string;
}

interface SharePointSite {
  id: string;
  name: string;
  displayName: string;
  webUrl: string;
}

interface CreateSiteParams {
  name: string;
  description: string;
}

// SharePoint configuration - update these with your values
const config: SharePointConfig = {
  siteId: import.meta.env.VITE_SHAREPOINT_SITE_ID || '',
  driveId: import.meta.env.VITE_SHAREPOINT_DRIVE_ID || '',
  templatesFolder: 'Templates',
  generatedContractsFolder: 'Generated Contracts',
  signedContractsFolder: 'Signed Contracts',
};

export const sharepointService = {
  /**
   * Start Microsoft OAuth flow
   */
  async startOAuthFlow(): Promise<void> {
    const clientId = import.meta.env.VITE_MICROSOFT_CLIENT_ID;
    const redirectUri = import.meta.env.VITE_SHAREPOINT_REDIRECT_URI || 
                        `${window.location.origin}/settings/connections/sharepoint/callback`;
    
    console.log('🔍 SharePoint OAuth Debug:', {
      clientId,
      redirectUri,
      hasClientId: !!clientId,
      env: import.meta.env,
    });
    
    if (!clientId) {
      throw new Error('Microsoft Client ID not configured. Make sure VITE_MICROSOFT_CLIENT_ID is set in .env.local and restart dev server.');
    }

    // Generate state for CSRF protection
    const state = crypto.randomUUID();
    sessionStorage.setItem('sharepoint_oauth_state', state);

    // Get tenant ID (use 'common' for multi-tenant or specific tenant ID for single-tenant)
    // If not specified, default to 'organizations' (any Azure AD tenant)
    const tenantId = import.meta.env.VITE_MICROSOFT_TENANT_ID || 'organizations';

    // Microsoft OAuth authorization URL (tenant-specific)
    const authUrl = new URL(`https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`);
    authUrl.searchParams.append('client_id', clientId);
    authUrl.searchParams.append('response_type', 'code');
    authUrl.searchParams.append('redirect_uri', redirectUri);
    authUrl.searchParams.append('response_mode', 'query');
    authUrl.searchParams.append('scope', 'Files.ReadWrite.All Sites.ReadWrite.All Mail.Send offline_access');
    authUrl.searchParams.append('state', state);

    console.log('🔐 Starting SharePoint OAuth flow...');
    console.log('📍 Redirect URI:', redirectUri);
    console.log('🔗 Auth URL:', authUrl.toString());
    
    window.location.href = authUrl.toString();
  },

  /**
   * Complete OAuth flow by exchanging code for tokens
   */
  async completeOAuthFlow(code: string, state: string): Promise<void> {
    // Verify state to prevent CSRF
    const savedState = sessionStorage.getItem('sharepoint_oauth_state');
    if (state !== savedState) {
      throw new Error('Invalid state parameter');
    }
    sessionStorage.removeItem('sharepoint_oauth_state');

    const redirectUri = import.meta.env.VITE_SHAREPOINT_REDIRECT_URI || 
                        `${window.location.origin}/settings/connections/sharepoint/callback`;

    // Call Edge Function to exchange code for tokens
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-oauth`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ code, redirectUri }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to complete OAuth');
    }

    const data = await response.json();
    console.log('✅ SharePoint OAuth completed:', data);
  },

  /**
   * List user's SharePoint sites
   */
  async listSites(connectionId: string): Promise<SharePointSite[]> {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-list-sites`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ connectionId }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list sites');
    }

    const data = await response.json();
    return data.sites;
  },

  /**
   * Create a new SharePoint site
   */
  async createSite(connectionId: string, params: CreateSiteParams): Promise<SharePointSite> {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-create-site`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ connectionId, ...params }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to create site');
    }

    const data = await response.json();
    return data.site;
  },

  /**
   * Setup folder structure in a site
   */
  async setupFolders(connectionId: string, siteId: string): Promise<void> {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-setup-folders`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ connectionId, siteId }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to setup folders');
    }
  },

  /**
   * Test connection
   */
  async testConnection(connectionId: string): Promise<boolean> {
    try {
      const session = await supabase.auth.getSession();
      const token = session.data.session?.access_token;

      const response = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-test-connection`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify({ connectionId }),
        }
      );

      return response.ok;
    } catch (error) {
      console.error('Test connection failed:', error);
      return false;
    }
  },

  /**
   * Delete connection
   */
  async deleteConnection(connectionId: string): Promise<void> {
    await connectionService.deleteConnection(connectionId);
  },
  /**
   * Upload a file to SharePoint
   */
  async uploadFile(
    folder: 'templates' | 'generated-contracts' | 'signed-contracts',
    fileName: string,
    fileContent: Blob | File
  ): Promise<{ path: string; webUrl: string }> {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    // Call Edge Function to handle SharePoint upload
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-upload`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          folder,
          fileName,
          fileContent: await fileContent.arrayBuffer(),
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to upload to SharePoint');
    }

    return response.json();
  },

  /**
   * Get a file download URL from SharePoint
   */
  async getFileUrl(filePath: string): Promise<string> {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-get-url`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ filePath }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to get SharePoint URL');
    }

    const data = await response.json();
    return data.url;
  },

  /**
   * List files in a SharePoint folder
   */
  async listFiles(folder: 'templates' | 'generated-contracts' | 'signed-contracts') {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-list-files`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ folder }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to list SharePoint files');
    }

    return response.json();
  },

  /**
   * Delete a file from SharePoint
   */
  async deleteFile(filePath: string): Promise<void> {
    const session = await supabase.auth.getSession();
    const token = session.data.session?.access_token;

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/sharepoint-delete`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({ filePath }),
      }
    );

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Failed to delete from SharePoint');
    }
  },
};

